export const DEFAULT_PRIMARY = "#124E5D";
export const DEFAULT_SECONDARY = "#FFBD55";
export const LIGHT_ON_BRAND = "#FEFEFE";
export const DARK_ON_BRAND = "#2c2c2c";

const WHITE = "#FFFFFF";
const BRAND_HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const WCAG_LINEAR_THRESHOLD = 0.03928;

export type TBrandContrastText = typeof LIGHT_ON_BRAND | typeof DARK_ON_BRAND;

type TRgb = {
  r: number;
  g: number;
  b: number;
};

export const isBrandHex = (color: string): boolean =>
  BRAND_HEX_PATTERN.test(color);

export const toBrandHex = (color: string): string | undefined => {
  if (typeof color !== "string") return undefined;
  const match = BRAND_HEX_PATTERN.exec(color);
  const hex = match?.[0];
  if (!hex) return undefined;
  return hex.toUpperCase();
};

export const isUnsetBrandColour = (color?: string | null): boolean =>
  color == null || color === "";

/**
 * `null` clears a colour. Anything other than #RRGGBB is omitted so a
 * half-typed value cannot be saved with another card.
 */
export const normalizeBrandColourForApi = (
  color: string | null | undefined,
): string | null | undefined => {
  if (color == null) return color;
  if (color === "") return null;
  return toBrandHex(color);
};

export const shownBrandHex = (
  color: string | null | undefined,
  fallback: string,
): string => {
  if (color == null || color === "") return fallback;
  return toBrandHex(color) ?? fallback;
};

const parseHex = (value: unknown): TRgb | undefined => {
  if (typeof value !== "string") return undefined;
  const match = /^#([0-9a-fA-F]{6})$/.exec(value);
  const hex = match?.[1];
  if (!hex) return undefined;
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
};

const toHex = ({ r, g, b }: TRgb): string => {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${channel(r)}${channel(g)}${channel(b)}`;
};

const linearize = (channel: number) => {
  const srgb = channel / 255;
  if (srgb <= WCAG_LINEAR_THRESHOLD) return srgb / 12.92;
  return ((srgb + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = ({ r, g, b }: TRgb) =>
  0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);

const contrastRatio = (foreground: TRgb, background: TRgb) => {
  const lighter = Math.max(
    relativeLuminance(foreground),
    relativeLuminance(background),
  );
  const darker = Math.min(
    relativeLuminance(foreground),
    relativeLuminance(background),
  );
  return (lighter + 0.05) / (darker + 0.05);
};

/** WCAG contrast of a #RRGGBB colour on white. Invalid input returns 0. */
export const contrastAgainstWhite = (color: string): number => {
  const rgb = parseHex(color);
  const white = parseHex(WHITE);
  if (!rgb || !white) return 0;
  return contrastRatio(rgb, white);
};

export const contrastTextOn = (color: string): TBrandContrastText => {
  const rgb = parseHex(color);
  const light = parseHex(LIGHT_ON_BRAND);
  const dark = parseHex(DARK_ON_BRAND);
  if (!rgb || !light || !dark) return LIGHT_ON_BRAND;
  const onLight = contrastRatio(rgb, light);
  const onDark = contrastRatio(rgb, dark);
  return onLight >= onDark ? LIGHT_ON_BRAND : DARK_ON_BRAND;
};

const rgbToHsl = ({ r, g, b }: TRgb) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: lightness };

  const delta = max - min;
  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  const hueBase = hueFromChannel({ max, red, green, blue, delta });
  return { h: hueBase / 6, s: saturation, l: lightness };
};

const hueFromChannel = ({
  max,
  red,
  green,
  blue,
  delta,
}: {
  max: number;
  red: number;
  green: number;
  blue: number;
  delta: number;
}) => {
  if (max === red) {
    const hue = (green - blue) / delta;
    return green < blue ? hue + 6 : hue;
  }
  if (max === green) return (blue - red) / delta + 2;
  return (red - green) / delta + 4;
};

const hueToRgb = (p: number, q: number, t: number) => {
  let channel = t;
  if (channel < 0) channel += 1;
  if (channel > 1) channel -= 1;
  if (channel < 1 / 6) return p + (q - p) * 6 * channel;
  if (channel < 1 / 2) return q;
  if (channel < 2 / 3) return p + (q - p) * (2 / 3 - channel) * 6;
  return p;
};

const hslToRgb = (h: number, s: number, l: number): TRgb => {
  if (s === 0) {
    const gray = l * 255;
    return { r: gray, g: gray, b: gray };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hueToRgb(p, q, h + 1 / 3) * 255,
    g: hueToRgb(p, q, h) * 255,
    b: hueToRgb(p, q, h - 1 / 3) * 255,
  };
};

const MIN_LINK_CONTRAST = 4.5;
const LIGHTNESS_STEP = 0.01;

/**
 * Same hue, darkened until text on white reaches 4.5:1.
 * A colour that already meets that ratio is returned unchanged.
 */
export const darkenForLink = (color: string): string => {
  const rgb = parseHex(color);
  if (!rgb) return color;
  if (contrastAgainstWhite(color) >= MIN_LINK_CONTRAST) return toHex(rgb);

  const { h, s, l } = rgbToHsl(rgb);
  let low = 0;
  let high = l;
  let bestLightness = 0;
  for (let step = 0; step < 24; step += 1) {
    const mid = (low + high) / 2;
    const candidate = toHex(hslToRgb(h, s, mid));
    if (contrastAgainstWhite(candidate) >= MIN_LINK_CONTRAST) {
      bestLightness = mid;
      low = mid;
    } else {
      high = mid;
    }
  }

  let lightness = bestLightness;
  let hex = toHex(hslToRgb(h, s, lightness));
  while (contrastAgainstWhite(hex) < MIN_LINK_CONTRAST && lightness > 0) {
    lightness = Math.max(0, lightness - LIGHTNESS_STEP);
    hex = toHex(hslToRgb(h, s, lightness));
  }
  return hex;
};
